"use client";
import React from "react";

type Section = { id: string; label: string };

export default function LegalLayout({
  title,
  updated,
  sections,
  children,
}: {
  title: string;
  updated?: string;
  sections: Section[];
  children: React.ReactNode;
}) {
  const [active, setActive] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!sections?.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            const id = e.target.getAttribute('id');
            if (id) setActive(id);
          }
        }
      },
      { rootMargin: '0px 0px -70% 0px', threshold: [0, 1] }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [sections]);

  return (
    <div className="container mx-auto px-4 md:px-6 py-6">
      <div className="max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">{title}</h1>
          {updated ? (
            <p className="mt-1 text-xs text-muted-foreground">Last updated: {updated}</p>
          ) : null}
        </header>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3 lg:sticky top-header self-start">
            <nav aria-label="Table of contents" className="rounded-lg border border-border bg-card p-3">
              <ol className="space-y-1 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`block px-2 py-1 rounded hover:bg-accent ${active === s.id ? 'bg-accent/60 text-foreground' : 'text-muted-foreground'}`}
                      aria-current={active === s.id ? 'location' : undefined}
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => { try { window.print(); } catch {} }} className="text-xs underline text-muted-foreground">Print</button>
              <span aria-hidden className="text-muted-foreground">•</span>
              <a href="#page-title" className="text-xs underline text-muted-foreground">Back to top</a>
            </div>
          </aside>
          <article className="lg:col-span-9 space-y-8 leading-relaxed text-foreground">
            {children}
          </article>
        </div>
      </div>
    </div>
  );
}

